import { Button, Divider, Modal, Radio } from 'antd';
import {
    PPA_PERF_SUMMARY_EXPORT_CANCEL_BTN,
    PPA_PERF_SUMMARY_EXPORT_CONFIRM_BTN,
    PPA_PERF_SUMMARY_EXPORT_MODAL,
    ppaPerfSummaryExportOptionTestId,
} from 'constants/ppaTestIds';
import { PS_FEATURE_ID_OVERALL_PERFORMANCE, PS_FEATURE_ID_PERFORMANCE_BY_LENS } from 'constants/ppa';

export type ExportOption = 'kpis' | typeof PS_FEATURE_ID_OVERALL_PERFORMANCE | typeof PS_FEATURE_ID_PERFORMANCE_BY_LENS;

const EXPORT_OPTIONS: Array<{ value: ExportOption; label: string }> = [
    { value: 'kpis', label: 'KPIs' },
    { value: PS_FEATURE_ID_OVERALL_PERFORMANCE, label: 'Overall Performance' },
    { value: PS_FEATURE_ID_PERFORMANCE_BY_LENS, label: 'Performance by Lens' },
];

type ExportModalProps = {
    open: boolean;
    selectedExportOption: ExportOption | null;
    onClose: () => void;
    onOptionChange: (value: ExportOption) => void;
    onExport: () => void;
};

const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.00016 1.33325C8.36835 1.33325 8.66683 1.63173 8.66683 1.99992V9.72378L11.5288 6.86185C11.7891 6.6015 12.2112 6.6015 12.4716 6.86185C12.7319 7.1222 12.7319 7.54431 12.4716 7.80466L8.47157 11.8047C8.21122 12.065 7.78911 12.065 7.52876 11.8047L3.52876 7.80466C3.26841 7.54431 3.26841 7.1222 3.52876 6.86185C3.78911 6.6015 4.21122 6.6015 4.47157 6.86185L7.3335 9.72378V1.99992C7.3335 1.63173 7.63197 1.33325 8.00016 1.33325ZM1.3335 13.9999C1.3335 13.6317 1.63197 13.3333 2.00016 13.3333H14.0002C14.3684 13.3333 14.6668 13.6317 14.6668 13.9999C14.6668 14.3681 14.3684 14.6666 14.0002 14.6666H2.00016C1.63197 14.6666 1.3335 14.3681 1.3335 13.9999Z"
            fill="#71717B"
        />
    </svg>
);

const PerformanceSummaryExportModal = ({
    open,
    selectedExportOption,
    onClose,
    onOptionChange,
    onExport,
}: ExportModalProps) => {
    const isExportDisabled = !selectedExportOption;

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            destroyOnClose
            centered
            width={352}
            onCancel={onClose}
            bodyStyle={{ padding: 24 }}
            modalRender={(modalNode) => (
                <div data-testid={PPA_PERF_SUMMARY_EXPORT_MODAL}>{modalNode}</div>
            )}
        >
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between h-6">
                    <div className="flex items-center gap-2">
                        <DownloadIcon />
                        <span className="text-[17px] leading-normal font-medium text-[#140B1D]">Export</span>
                    </div>
                </div>

                <Divider className="!my-0 !border-[#f0f0f0]" />

                <div className="flex flex-col gap-2">
                    <div className="h-8 flex items-center text-sm leading-[22px] text-black/25">
                        Choose export options
                    </div>
                    <Radio.Group
                        value={selectedExportOption}
                        onChange={(event) => onOptionChange(event.target.value as ExportOption)}
                        className="w-full [&_.ant-radio-wrapper]:!m-0 [&_.ant-radio-wrapper]:!inline-flex [&_.ant-radio-wrapper]:!items-center [&_.ant-radio-wrapper]:!leading-[22px] [&_.ant-radio]:!top-0"
                    >
                        <div className="flex flex-col items-start gap-2">
                            {EXPORT_OPTIONS.map((option) => (
                                <Radio
                                    key={option.value}
                                    className="!m-0 !inline-flex !items-center"
                                    value={option.value}
                                    data-testid={ppaPerfSummaryExportOptionTestId(option.value)}
                                >
                                    <span className="text-[14px] leading-[22px] text-black/88">{option.label}</span>
                                </Radio>
                            ))}
                        </div>
                    </Radio.Group>
                </div>

                <Divider className="!my-0 !border-[#f0f0f0]" />

                <div className="flex justify-end items-center gap-3">
                    <Button
                        onClick={onClose}
                        type="default"
                        style={{ borderRadius: 6 }}
                        data-testid={PPA_PERF_SUMMARY_EXPORT_CANCEL_BTN}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        disabled={isExportDisabled}
                        onClick={onExport}
                        style={{ borderRadius: 6 }}
                        data-testid={PPA_PERF_SUMMARY_EXPORT_CONFIRM_BTN}
                    >
                        Export
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PerformanceSummaryExportModal;
